import { makeWorkbook } from "../../utils/excel";
import { getIndicatorRestitutions } from "../../../../../../rest/indicator";

function causeToLabel(cause, intl) {
  const FALLBACK = (cause + "").replaceAll("_", " ");
  if (!cause || !intl) return cause ? FALLBACK : "Sem motivo";

  const I18N = {
    FAILURE_TO_CREATE_FULL_TIME_JOB:
      "noncompliance.cause.FAILURE_TO_CREATE_FULL_TIME_JOB",
    FAILURE_TO_INVEST_CREATE_JOBS:
      "noncompliance.cause.FAILURE_TO_INVEST_CREATE_JOBS",
    FAILURE_TO_MAINTAIN_ACTIVITY:
      "noncompliance.cause.FAILURE_TO_MAINTAIN_ACTIVITY",
    FAILURE_TO_MAINTAIN_JOBS_2_YEARS:
      "noncompliance.cause.FAILURE_TO_MAINTAIN_JOBS_2_YEARS",
    UNEMPLOYMENT_BENEFICIARY_LOST_JOB:
      "noncompliance.cause.UNEMPLOYMENT_BENEFICIARY_LOST_JOB",
    FALSE_DECLARATIONS: "noncompliance.cause.FALSE_DECLARATIONS",
    CONTROLLING_SHARE_LOSS: "noncompliance.cause.CONTROLLING_SHARE_LOSS",
    PROMOTER_SHARE_SOLD: "noncompliance.cause.PROMOTER_SHARE_SOLD",
    PERSISTENT_IRREGULARITY: "noncompliance.cause.PERSISTENT_IRREGULARITY",
    MISUSED_FUNDS: "noncompliance.cause.MISUSED_FUNDS",
    UNPAID_WAGES: "noncompliance.cause.UNPAID_WAGES",
    NON_COMPLIANT_WITH_REQUIREMENTS_OBLIGATIONS: "noncompliance.cause.NON_COMPLIANT_WITH_REQUIREMENTS_OBLIGATIONS",
    VERIFIED_IRREGULARITY: "noncompliance.cause.VERIFIED_IRREGULARITY",
    UNDUE_AID_ACCUMULATION: "noncompliance.cause.UNDUE_AID_ACCUMULATION",
  };

  const id = I18N[cause] || null;
  if (!id) return FALLBACK;
  return intl.formatMessage({ id, defaultMessage: FALLBACK });
}

const safeArray = (x) => (Array.isArray(x) ? x : []);

function makeSheetNamer() {
  const used = new Set();
  return (raw) => {
    const bad = /[\\\/\?\*\:\[\]]/g;
    let name = String(raw || "")
      .replace(bad, " ")
      .trim();
    if (name.length > 31) name = name.slice(0, 31);
    let base = name || "Sheet";
    let out = base;
    let n = 2;
    while (used.has(out)) {
      const sfx = ` (${n})`;
      out = base.slice(0, 31 - sfx.length) + sfx;
      n++;
    }
    used.add(out);
    return out;
  };
}

export async function buildWorkbookNonCompliance(opts = {}) {
  const { intl } = opts;

  const resp = await getIndicatorRestitutions();
  const payload = resp?.data || {};
  const summary = Array.isArray(payload?.data)
    ? payload.data[0]
    : payload?.data || {};

  const asked = Number(summary?.asked || 0);
  const payed = Number(summary?.payed || 0);
  const total = Number(summary?.total || 0);

  const block = (en, pt) => safeArray(summary?.[en] ?? summary?.[pt]);
  const comTot = block("comTot", "revogacaoTotalComRestituicao");
  const comPar = block("comPar", "revogacaoParcialComRestituicao");
  const comSem = block("comSem", "semRevogacaoComRestituicao");
  const semTot = block("semTot", "revogacaoTotalSemRestituicao");
  const semPar = block("semPar", "revogacaoParcialSemRestituicao");
  const semSem = block("semSem", "semRevogacaoSemRestituicao");

  const L = {
    resumo: intl
      ? intl.formatMessage({ id: "resume.kpis", defaultMessage: "Resumo" })
      : "Resumo",
    totalInc: intl
      ? intl.formatMessage({
          id: "indicators.total.noncompliance",
          defaultMessage: "Total Incumprimentos",
        })
      : "Total Incumprimentos",
    asked: intl
      ? intl.formatMessage({
          id: "indicators.restitutions.asked",
          defaultMessage: "Restituições Pedidas",
        })
      : "Restituições Pedidas",
    payed: intl
      ? intl.formatMessage({
          id: "indicators.restitutions.payed",
          defaultMessage: "Restituições Pagas",
        })
      : "Restituições Pagas",
    motivo: intl
      ? intl.formatMessage({
          id: "noncompliance.reason",
          defaultMessage: "Motivo",
        })
      : "Motivo",
    nproc: intl
      ? intl.formatMessage({
          id: "candidatureNumber.column",
          defaultMessage: "Nº Processos",
        })
      : "Nº Processos",
    valor: intl
      ? intl.formatMessage({
          id: "noncompliance.valueToReturn",
          defaultMessage: "Valor a Restituir",
        })
      : "Valor a Restituir",
    comRest: intl
      ? intl.formatMessage({
          id: "noncompliance.withRestitution",
          defaultMessage: "Com Restituição",
        })
      : "Com Restituição",
    semRest: intl
      ? intl.formatMessage({
          id: "noncompliance.withoutRestitution",
          defaultMessage: "Sem Restituição",
        })
      : "Sem Restituição",
    revTotal: intl
      ? intl.formatMessage({
          id: "noncompliance.revocation.total",
          defaultMessage: "Revogação Total",
        })
      : "Revogação Total",
    revParc: intl
      ? intl.formatMessage({
          id: "noncompliance.revocation.partial",
          defaultMessage: "Revogação Parcial",
        })
      : "Revogação Parcial",
    semRev: intl
      ? intl.formatMessage({
          id: "noncompliance.revocation.none",
          defaultMessage: "Sem Revogação",
        })
      : "Sem Revogação",
  };

  const nameSheet = makeSheetNamer();

  const resumoAoa = [
    ["Indicador", "Valor"],
    [L.totalInc, total],
    [L.asked, asked],
    [L.payed, payed],
  ];

  const makeSheetWithValue = (rows, title) => {
    const items = safeArray(rows);
    return {
      name: nameSheet(title),
      aoa: [
        [L.motivo, L.nproc, L.valor],
        ...items.map((r) => [
          causeToLabel(r.cause, intl),
          Number(r.count || 0),
          Number(r.askedSum || 0),
        ]),
        [
          "Total",
          items.reduce((s, r) => s + (Number(r.count) || 0), 0),
          items.reduce((s, r) => s + (Number(r.askedSum) || 0), 0),
        ],
      ],
    };
  };

  const makeSheetNoValue = (rows, title) => {
    const items = safeArray(rows);
    return {
      name: nameSheet(title),
      aoa: [
        [L.motivo, L.nproc],
        ...items.map((r) => [
          causeToLabel(r.cause, intl),
          Number(r.count || 0),
        ]),
        ["Total", items.reduce((s, r) => s + (Number(r.count) || 0), 0)],
      ],
    };
  };

  return makeWorkbook([
    { name: nameSheet(L.resumo), aoa: resumoAoa },
    makeSheetWithValue(comTot, `${L.comRest} - ${L.revTotal}`),
    makeSheetWithValue(comPar, `${L.comRest} - ${L.revParc}`),
    makeSheetWithValue(comSem, `${L.comRest} - ${L.semRev}`),
    makeSheetNoValue(semTot, `${L.semRest} - ${L.revTotal}`),
    makeSheetNoValue(semPar, `${L.semRest} - ${L.revParc}`),
    makeSheetNoValue(semSem, `${L.semRest} - ${L.semRev}`),
  ]);
}
